import type { Designer } from "@wix/ambassador-picasso-editor-v1-designer/types";
import { Box, Text } from "@wix/design-system";
import { useTranslation, useExperiments } from "@wix/fe-essentials-standalone";
import { DesignerModal, useTheme } from "@wix/picasso-common";
import { type FC, type RefObject } from "react";
import { ErrorBoundary } from "../Common/ErrorBoundry";
import { useAppContext } from "../Common/useAppContext";
import { Header } from "../Header/Header";
import { QuickStart } from "../QuickStart/QuickStart";
import { SuggestionButtons } from "../SuggestionButtons/SuggestionButtons";
import { InputPanel } from "./InputPanel";
import "./LandingPage.css";
import { SpeechError } from "./SpeechError";
import { Subtitle } from "./Subtitle";
import { Title } from "./Title";
import { useLandingPage } from "./useLandingPage";
import { useMobile } from "../../../hooks/useMobile";
import { useDesignerModalBiReporter } from "./useDesignerModalBiReporter";
import {
  FORCE_HEBREW_EXPERIMENT,
  BUDGET_EXCEEDED_EXPERIMENT,
  INITIAL_GENERATION_NEW_FLOW_EXPERIMENT,
} from "../../../consts";
import { RecreateButtons } from "../Recreate/RecreateButtons";
import { BudgetExceeded } from "../BudgetExceeded/budgetExceeded";

interface LandingPageProps {
  userDetails?: UserDetails;
  designers?: Designer[];
  isBudgetExceeded?: boolean;
}

const LandingPage: FC<LandingPageProps> = ({ designers, isBudgetExceeded }) => {
  const {
    handleGenerateSiteClick,
    generatingProject,
    prompt,
    setPrompt,
    fileInputRef,
    onFileAttachment,
    attachmentPreviews,
    isDesignStudioModalOpen,
    setIsDesignStudioModalOpen,
    handleDesignStudioSelected,
    sizeMapByElementType,
    toggleMicrophone,
    isListening,
    speechError,
    setSelectedDesignStudio,
    selectedDesignStudio,
    handleDesignStudioClick,
    handleFileAttachmentClick,
    setSuggestion,
    isMicPermissionDenied,
    selectedDesignerName,
  } = useLandingPage(designers || []);
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { isLoggedIn } = useAppContext();
  const { theme } = useTheme();
  const isMobile = useMobile();
  const biReporter = useDesignerModalBiReporter();
  const isForceHebrewEnabled = experiments.enabled(FORCE_HEBREW_EXPERIMENT);

  const isShowRecreateButtons = experiments.enabled(
    INITIAL_GENERATION_NEW_FLOW_EXPERIMENT,
  );

  const isCheckBudgetLimitEnabled = experiments.enabled(
    BUDGET_EXCEEDED_EXPERIMENT,
  );

  const shouldShowBudgetExceeded =
    isCheckBudgetLimitEnabled && isBudgetExceeded;
  return (
    <Box direction="vertical" width="100%" boxSizing="border-box">
      <Box
        className={`landing-page-container ${theme}`}
        width="100%"
        direction="vertical"
        height={!isLoggedIn ? "100dvh" : undefined}
      >
        <ErrorBoundary>
          <Header isLoggedIn={isLoggedIn} />
        </ErrorBoundary>
        <Box
          height={isMobile ? "100%" : undefined}
          padding={isMobile ? "16px" : "20px 20px 0 20px"}
          paddingTop={isMobile ? "30px" : "60px"}
          align="center"
          gap="4px"
          direction="vertical"
        >
          <Box maxWidth={isMobile ? "70vw" : "100%"} align="center">
            <Title size={sizeMapByElementType.title} />
          </Box>
          <Box marginTop={1}>
            <Subtitle />
          </Box>
          <Box
            width={isMobile ? "calc(100% - 32px)" : "80%"}
            maxWidth="846px"
            marginTop={isMobile ? "24px" : "40px"}
            direction="vertical"
            padding={4}
            borderRadius="12px"
            background="var(--chat-container-background)"
            align="center"
          >
            <InputPanel
              generatingProject={generatingProject}
              handleGenerateSiteClick={handleGenerateSiteClick}
              prompt={prompt}
              setPrompt={setPrompt}
              onFileAttachment={onFileAttachment}
              fileInputRef={fileInputRef as RefObject<HTMLInputElement>}
              attachmentPreviews={attachmentPreviews}
              handleFileAttachmentClick={handleFileAttachmentClick}
              selectedDesignStudio={selectedDesignStudio}
              handleDesignStudioClick={handleDesignStudioClick}
              setSelectedDesignStudio={setSelectedDesignStudio}
              sizeMapByElementType={sizeMapByElementType}
              toggleMicrophone={toggleMicrophone}
              isListening={isListening}
              isMicPermissionDenied={isMicPermissionDenied}
              selectedDesignerName={selectedDesignerName}
              isBudgetExceeded={shouldShowBudgetExceeded}
            />

            <ErrorBoundary>
              {shouldShowBudgetExceeded ? (
                <BudgetExceeded />
              ) : isShowRecreateButtons ? (
                <RecreateButtons />
              ) : (
                <SuggestionButtons onSelectPrompt={setSuggestion} />
              )}
            </ErrorBoundary>
          </Box>
          <Box
            maxWidth={isMobile ? "100%" : "80%"}
            align="center"
            textAlign="center"
            paddingTop={2}
          >
            <Text size="tiny" secondary>
              {t("landing.ai.disclaimer")}
            </Text>
          </Box>
          {isForceHebrewEnabled && (
            <Box marginTop={4}>
              <Text
                size={isMobile ? "small" : "medium"}
                className="landing-subtitle"
              >
                {t("landing.students.unique-email")}
              </Text>
            </Box>
          )}

          {speechError && <SpeechError message={speechError} />}
        </Box>
        {isLoggedIn && <QuickStart />}
      </Box>
      <ErrorBoundary>
        <DesignerModal
          isOpen={isDesignStudioModalOpen}
          onClose={() => setIsDesignStudioModalOpen(false)}
          onDesignerSelected={handleDesignStudioSelected}
          initialSelectedDesigner={selectedDesignStudio}
          designers={designers}
          translations={{
            title: t("landing.design_studio.title"),
            subtitle: t("landing.design_studio.subtitle"),
            selectDesigner: t("landing.design_studio.selectDesigner"),
            mobileTitle: t("landing.design_studio.mobileTitle"),
            mobileSubtitle: t("landing.design_studio.mobileSubtitle"),
            designedBy: t("landing.design_studio.designedBy"),
            seeMore: t("landing.design_studio.seeMore"),
            seeLess: t("landing.design_studio.seeLess"),
          }}
          t={t}
          onBiEvent={biReporter}
        />
      </ErrorBoundary>
    </Box>
  );
};

export default LandingPage;
